import type { DataTableColumnWidths } from "building-types/dataTables/dataTable";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { BackgroundVisibility } from "building-types/settings/sheetsTypesEnum";
import type { SheetParameterValues } from "building-types/systemParameterTypes";
import type { PlanType } from "building-types/views/BuiltinPlanType";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import type {
  DrawingViewTemplate,
  ElevationViewTemplate,
  EvaluatedDataTable,
  EvaluatedViewport,
  PerspectiveViewTemplate,
  PlanViewTemplate,
  SectionViewTemplate,
} from "building-types/views/planSets";
import type { SheetGeometry, SheetSVGGeometry, SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import type { SectionViewGeometry } from "building-types/views/View3D";
import type { ViewStyle } from "building-types/views/ViewStyle";
import type { Table } from "common/data/table";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import type { TextAlignment } from "common/utils/textAlignment";
import type { PerspectiveDrawingCamera } from "doc/plans/drawingCameraTypes";
import type { DrawingLayout, LayoutSettings } from "doc/plans/layout";
import type { GeometryStyleSheet } from "doc/plans/primitives/primitiveStylesTypes";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import type { Mat3 } from "geom/mat3";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec3 } from "geom/vec3";
import type { CachedBuildingBSPOptions } from "../pdf/planSetBSPProvider";
import type { Linework3DRenderingConfig } from "./Linework3DRenderingConfig";
import type { SheetNodeStyleSheet } from "./nodes/nodeStylesTypes";
import type { TableCellContentType } from "./nodes/tableCellTypes";
import type { SheetTitle } from "./sheetInfo";
import type { SheetNodeType } from "./sheetNodeType";

// Re-export SheetNodeType for backwards compatibility
export { SheetNodeType } from "./sheetNodeType";

export interface SheetNodeTransform {
  readonly translateX?: () => number; // x is ignored if func exists
  readonly translateY?: () => number; // y is ignored if func exists
}

export interface SheetNodeRenderedBounds {
  /** @deprecated Avoid using SheetNodeRenderedBounds - layout should be resolved statically */
  renderedHeight?: number;
}

/** @deprecated Avoid using SheetNodeRenderedBounds - layout should be resolved statically */
export function renderedNodeBoundsNew(): SheetNodeRenderedBounds {
  return {
    renderedHeight: undefined,
  };
}

interface SheetNodeBase {
  readonly handle?: DocEntityHandle;
  readonly rotation?: number; // As radians
  readonly x?: SheetInches;
  readonly y?: SheetInches;
  readonly transform?: SheetNodeTransform; // TODO: remove this
  readonly renderedBounds?: SheetNodeRenderedBounds; // TODO: remove this
}

export interface SheetNodeSize {
  // TODO - make these immutable - right now they are mutated in various places
  width?: SheetInches;
  height?: SheetInches;
}

interface ScaledSheetNodeBase extends SheetNodeBase, Required<SheetNodeSize> {
  readonly layoutSettings?: LayoutSettings | DrawingLayout | undefined;
}

export interface GeometrySheetNodeBase<T extends SheetGeometry> extends SheetNodeBase {
  readonly type: SheetNodeType.GEOMETRY;
  readonly geometry: T;
}

export interface GeometrySheetNode extends GeometrySheetNodeBase<SheetGeometry> {
  readonly style?: GeometryStyleSheet;
}

interface GroupBase extends SheetNodeBase {
  readonly clippingPath?: PolyLine2;
  readonly children: SheetNode[];
}

export interface Group extends GroupBase, SheetNodeSize {
  readonly type: SheetNodeType.GROUP;
}

export interface Page extends GroupBase, SheetNodeSize {
  readonly type: SheetNodeType.PAGE;
  readonly sheetTitle: SheetTitle;
  readonly hash: string; // uniquely identifies a Page for generating and persisting sheet views
  readonly margin?: number;
  readonly docSheetId?: string; // the id of the docEntity that this page is created from
  readonly sheetParameterValues?: Partial<SheetParameterValues>;
}

export interface TableSheetNode extends SheetNodeBase, SheetNodeSize {
  readonly type: SheetNodeType.TABLE;
  readonly style?: SheetNodeStyleSheet;
  rows: TableCellSheetNode[][];
  readonly columnWidths?: DataTableColumnWidths;
  readonly title?: {
    text: string;
    fontSize: number;
    alignment?: TextAlignment;
  };
}

export interface DeferredTable extends SheetNodeBase {
  readonly type: SheetNodeType.DEFERRED_TABLE;
  readonly style?: SheetNodeStyleSheet;
  readonly dataTable: EvaluatedDataTable;
}

interface TableCellSheetNodeBase extends SheetNodeBase, SheetNodeSize {
  readonly type: SheetNodeType.TABLE_CELL;
  readonly style?: SheetNodeStyleSheet;
}

export interface TableCellTextSheetNode extends TableCellSheetNodeBase {
  readonly contentType: TableCellContentType.TEXT;
  readonly contents: GeometrySheetNodeBase<SheetTextGeometry>;
}

export interface TableCellSVGSheetNode extends TableCellSheetNodeBase {
  readonly contentType: TableCellContentType.SVG;
  readonly contents: GeometrySheetNodeBase<SheetSVGGeometry>;
}

export interface TableCellGroupSheetNode extends TableCellSheetNodeBase {
  readonly contentType: TableCellContentType.GROUP;
  readonly contents: Group;
}

export type TableCellSheetNode = TableCellTextSheetNode | TableCellSVGSheetNode | TableCellGroupSheetNode;
interface PlanBase extends ScaledSheetNodeBase, Required<SheetNodeSize> {
  readonly type: SheetNodeType.PLAN;
  readonly planRotationRads: number;
}

interface LevelPlanBase extends PlanBase {
  readonly levelId: string;
}

export function isLevelPlanBase(entity: PlanBase): entity is LevelPlanBase {
  return "levelId" in entity;
}

/**
 * Viewport plan renders a viewport of a user-specified view. It
 * has the capacity to replace all of the other drawing types as a viewport
 * can represent any option state or view configuration.
 */
export interface ViewportPlan extends Omit<LevelPlanBase, "layoutSettings" | "planRotationRads"> {
  readonly planType: PlanType.VIEWPORT_PLAN;
  readonly viewport: EvaluatedViewport;
  readonly viewTemplate:
    | PlanViewTemplate
    | ElevationViewTemplate
    | SectionViewTemplate
    | DrawingViewTemplate
    | PerspectiveViewTemplate;
  readonly sectionView?: SectionViewGeometry;
  readonly sheetTransform?: Mat3;
}

export interface FoundationPlan extends LevelPlanBase {
  planType: PlanType.FOUNDATION_PLAN;
  detailSheetTitle: SheetTitle | undefined;
}

interface FoundationUtilityPlan extends LevelPlanBase {
  planType: PlanType.FOUNDATION_UTILITY_PLAN;
  detailSheetTitle: SheetTitle;
}

interface StructuralPlan extends LevelPlanBase {
  planType: PlanType.STRUCTURAL_PLAN;
}

interface RoofPlan extends PlanBase {
  planType: PlanType.ROOF_PLAN;
}

interface BackgroundPlanBase {
  labels: Table<boolean>;
  backgroundVisibility: BackgroundVisibility;
}

interface RoofBackgroundPlan extends PlanBase, BackgroundPlanBase {
  planType: PlanType.ROOF_BACKGROUND_PLAN;
}

interface BackgroundPlan extends LevelPlanBase, BackgroundPlanBase {
  planType: PlanType.BACKGROUND_PLAN;
}

export interface FloorPlan extends LevelPlanBase {
  planType: PlanType.FLOOR_PLAN;
  includeProps?: boolean;
}

export interface SummaryFloorPlan extends LevelPlanBase {
  planType: PlanType.SUMMARY_PLAN;
}

export interface BrochureSummaryFloorPlan extends LevelPlanBase {
  planType: PlanType.BROCHURE_SUMMARY_PLAN;
  displayPlanLabelDims: boolean;
  displayElectricalOptions: boolean;
}

export interface BrochureSummaryElectricalFloorPlan extends LevelPlanBase {
  planType: PlanType.BROCHURE_SUMMARY_ELECTRICAL_PLAN;
  displayPlanLabelDims: boolean;
}

export interface FloorMaterialsPlan extends LevelPlanBase {
  planType: PlanType.FLOOR_MATERIALS_PLAN;
}

interface ElectricalPlan extends LevelPlanBase {
  planType: PlanType.ELECTRICAL_PLAN;
}

export interface ElevationAnnotationOverlay extends ScaledSheetNodeBase {
  type: SheetNodeType.ELEVATION_ANNOTATION_OVERLAY;

  eyePos: Vec3;

  visibilitySelectors?: SheetGeometrySelectorEffectMap[]; // Can affect prop visibility, used to determine building bounding box.

  includeGradeLine: boolean;
  allowBelowGroundAnnotations: boolean;
  referenceFrame: ElevationReferenceFrame;
}

export interface BrochureElevationOverlay extends ScaledSheetNodeBase {
  type: SheetNodeType.BROCHURE_ELEVATION_OVERLAY;
  eyePos: Vec3;
}

export interface LineworkDrawingBase {
  sceneOptions: CachedBuildingBSPOptions;
  occlusionSceneOptions?: CachedBuildingBSPOptions;
  useRoofVolumeBSP?: boolean;
  planRotationRads?: number;
  renderingConfig?: Linework3DRenderingConfig<any>;
  backgroundVisibility?: BackgroundVisibility;
  renderSize?: {
    width: number;
    height: number;
  };
  viewport?: EvaluatedViewport; // Only used in sheet templates.
  viewStyle?: ViewStyle;
  viewTemplateOverrideIds?: DocEntityId<"VIEW_TEMPLATE_OVERRIDE">[]; // From the view template, only used in sheet templates. VTOs from the viewport are also applied.
}

export enum AutoCrop {
  OFF,
  ON_FRACTIONAL_PADDING,
  ON_INCHES_PADDING,
}

export interface PerspectiveDrawing extends ScaledSheetNodeBase, LineworkDrawingBase {
  type: SheetNodeType.PERSPECTIVE_DRAWING;

  autoCrop: AutoCrop; // Set to true to automatically crop the camera to the building.

  // If autoCrop is on, this is the padding that will be added around the edges of the building. Either a fraction of the building size on screen or a number of inches.
  autoCropPadding?: number;

  // PerspectiveDrawing should define either camera or viewport.
  camera?: PerspectiveDrawingCamera;
}

export interface OrthographicDrawing extends ScaledSheetNodeBase, LineworkDrawingBase {
  type: SheetNodeType.ORTHOGRAPHIC_DRAWING;

  // OrthographicDrawing should define either eyePos or viewport.
  eyePos?: Vec3;

  upVector?: Vec3; // Defaults to 0, 0, 1 (elevation view)

  sectionView?: SectionViewGeometry;
  isElevation: boolean;
}

export type SheetNode =
  | Page
  | Group
  | TableSheetNode
  | TableCellSheetNode
  | DeferredTable
  | Plan
  | GeometrySheetNode
  | ElevationAnnotationOverlay
  | BrochureElevationOverlay
  | PerspectiveDrawing
  | OrthographicDrawing;

export type Plan =
  | FoundationPlan
  | FoundationUtilityPlan
  | StructuralPlan
  | FloorPlan
  | BackgroundPlan
  | SummaryFloorPlan
  | BrochureSummaryFloorPlan
  | BrochureSummaryElectricalFloorPlan
  | FloorMaterialsPlan
  | ElectricalPlan
  | RoofPlan
  | RoofBackgroundPlan
  | ViewportPlan;
